import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import * as S from './styles';
import { ModalProps } from './types';
import React, { useEffect, useRef, useCallback } from 'react';

export const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  headerIcon = faTimes,
  size = 'medium',
  customFooter,
  closeOnOverlayClick = true,
  noBodyScroll = false,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      handleClose();
    }
  }, [closeOnOverlayClick, handleClose]);

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={handleOverlayClick}>
      <S.ModalContainer ref={modalRef} size={size}>
        <S.ModalHeader>
          <S.ModalTitle>
            <FontAwesomeIcon icon={headerIcon as IconProp} />
            {title}
          </S.ModalTitle>
          <S.CloseButton onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </S.CloseButton>
        </S.ModalHeader>

        <S.ModalBody noScroll={noBodyScroll}>
          {children}
        </S.ModalBody>

        {customFooter && (
          <S.ModalFooter>
            {customFooter}
          </S.ModalFooter>
        )}
      </S.ModalContainer>
    </S.ModalOverlay>
  );
};
